import { useState } from "react";
import PropTypes from "prop-types";
import QuotationRoom from "app/pages/Booking/Quotation/Accommodation/QuotationRoom";
import { OFFER_CONTRACT_TYPES } from "app/constants";
import "./QuotationRooms.scss";
import { accommodationTypeShape, messagePropType } from "app/utils/propTypes";
import { useFsFlag } from "@flagship.io/react-sdk";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { sendTagOnAccommodationsShowMore } from "app/utils/analytics";
import { FormattedMessage } from "react-intl";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import { useFormikContext } from "formik";

const INITIAL_ACCOMMODATIONS_COUNT = 3;
const LOAD_MORE_INCREMENT = 3;

const QuotationRooms = ({
	onChange,
	contractType,
	accommodations = [],
	title,
	stressMarketingLabel,
}) => {
	const { values } = useFormikContext();

	const selectedAccommodationId = values?.accommodation;
	const selectedAccommodationIndex = accommodations.findIndex(
		accommodation => accommodation?.code === selectedAccommodationId
	);

	const [displayCount, setDisplayCount] = useState(
		selectedAccommodationIndex >= INITIAL_ACCOMMODATIONS_COUNT
			? Math.ceil((selectedAccommodationIndex + 1) / LOAD_MORE_INCREMENT) *
					LOAD_MORE_INCREMENT
			: INITIAL_ACCOMMODATIONS_COUNT
	);

	const hasMoreAccommodations = accommodations.length > displayCount;
	const showStressMarketingMsgFlag = useFsFlag("stress_marketing_message_display");
	const showStressMarketingMsg = showStressMarketingMsgFlag.getValue(false);

	const handleShowMore = () => {
		setDisplayCount(prevCount => prevCount + LOAD_MORE_INCREMENT);
		sendTagOnAccommodationsShowMore();
	};

	return (
		<div className="quotation-rooms">
			<Typography
				variant={TYPOGRAPHY_VARIANTS.LARGE}
				isBold
				className="quotation-rooms__subtitle"
				component="h3"
			>
				{title}
			</Typography>

			{accommodations.slice(0, displayCount).map(accommodation => {
				return (
					<div
						className="quotation-rooms__item"
						key={accommodation.code}
						data-testid="quotation-rooms-item"
					>
						<QuotationRoom
							contractType={contractType}
							key={accommodation.code}
							accommodation={accommodation}
							onChange={onChange}
							{...stressMarketingLabel &&
								showStressMarketingMsg && {
									stressMarketingLabel: stressMarketingLabel,
								}}
						/>
					</div>
				);
			})}

			{hasMoreAccommodations && (
				<Button
					className="quotation-rooms__show-more-button"
					variant={BUTTON_VARIANTS.STROKE}
					onClick={handleShowMore}
					data-testid="show-more-rooms-button"
					label={<FormattedMessage id="quotation.accommodation.view.more" />}
				/>
			)}
		</div>
	);
};

QuotationRooms.propTypes = {
	title: messagePropType,
	accommodations: PropTypes.arrayOf(PropTypes.shape(accommodationTypeShape)),
	onChange: PropTypes.func,
	contractType: PropTypes.oneOf([OFFER_CONTRACT_TYPES.HOTEL, OFFER_CONTRACT_TYPES.RENTAL]),
	stressMarketingLabel: PropTypes.string,
};

export default QuotationRooms;
